import axios from "axios";

// Mismo-origen salvo que se defina una base explícita (igual que pl-fe).
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 401 → sesión inválida (expiró o fue reemplazada por otro dispositivo):
// se limpia el token y App vuelve al login mediante el callback registrado.
let onAuthError = null;
export function setOnAuthError(fn) {
  onAuthError = fn;
}

api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || "";
    if (status === 401 && !url.startsWith("/auth/")) {
      localStorage.removeItem("access_token");
      if (onAuthError) onAuthError();
    }
    return Promise.reject(error);
  }
);

// ---- Sesión ----
export function getToken() {
  return localStorage.getItem("access_token");
}

export function logout() {
  localStorage.removeItem("access_token");
}

/**
 * Login del proveedor: email + contraseña.
 * Devuelve { mustChange: true } si el backend exige cambio de contraseña
 * (respuesta sin token), o { token } si el ingreso fue exitoso.
 */
export async function login(email, password) {
  const { data } = await api.post("/auth/login", {
    email: String(email).trim(),
    password,
  });
  if (data?.must_change_password) return { mustChange: true };
  const token = data?.access_token || data?.token;
  if (!token) throw new Error("Respuesta de login inválida");
  localStorage.setItem("access_token", token);
  return { token };
}

/** Cambio de contraseña forzado (sin token; valida con la contraseña actual). */
export async function changePassword(email, currentPassword, newPassword) {
  const { data } = await api.post("/auth/change-password", {
    email: String(email).trim(),
    current_password: currentPassword,
    new_password: newPassword,
  });
  return data;
}

// ---- Portal del proveedor (prefijo /api/sales) ----
export async function getMe() {
  const { data } = await api.get("/api/sales/provider/me");
  return data;
}

export async function getAvailability(date, originId, destinationId) {
  const { data } = await api.get("/api/sales/provider/availability", {
    params: { date, origin_id: originId, destination_id: destinationId },
  });
  return Array.isArray(data) ? data : data?.results || data?.items || [];
}

export async function createReservation(payload) {
  const { data } = await api.post("/api/sales/provider/reservations", payload);
  return data;
}

export async function listReservations(params = {}) {
  const { data } = await api.get("/api/sales/provider/reservations", { params });
  if (Array.isArray(data)) return data;
  return data?.items || data?.results || data?.reservations || [];
}

export async function getReservation(code) {
  const { data } = await api.get(
    `/api/sales/provider/reservations/${encodeURIComponent(code)}`
  );
  return data;
}

export async function cancelReservation(code, reason) {
  const { data } = await api.post(
    `/api/sales/provider/reservations/${encodeURIComponent(code)}/cancel`,
    { reason: reason || null }
  );
  return data;
}

/** Envía/reenvía el voucher por correo. Sin email usa el contacto del cupón. */
export async function sendVoucherEmail(code, email) {
  const { data } = await api.post(
    `/api/sales/provider/reservations/${encodeURIComponent(code)}/send-email`,
    { email: email || null }
  );
  return data;
}

export async function getMovements(params = {}) {
  const { data } = await api.get("/api/sales/provider/movements", { params });
  return data;
}

// ---- Portal de proveedores de REPUESTOS (prefijo /api/proveedor-repuestos) ----
// Cuentas con el permiso REPUESTOS_PORTAL (talleres, llanteras, almacenes).
const REP = "/api/proveedor-repuestos";

// DOS PORTALES DISTINTOS con este mismo código (Harry, 3-nov-2026): los CONVENIOS
// son aliados de ventas; los PROVEEDORES de los vehículos (repuestos) son otra cosa,
// con su propia dirección. Se compilan aparte y cada build solo deja entrar a sus cuentas:
//   REACT_APP_PORTAL=repuestos → build-repuestos (portal de proveedores de vehículos)
//   sin la variable           → build (portal de convenios, como siempre)
export const PORTAL = process.env.REACT_APP_PORTAL === "repuestos" ? "repuestos" : "convenios";

/** Permisos del token (claim "permissions" del JWT); [] si no se puede leer. */
export function getTokenPermissions() {
  const token = getToken();
  if (!token) return [];
  try {
    const part = (token.split(".")[1] || "").replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(part + "===".slice((part.length + 3) % 4));
    const json = new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
    const perms = JSON.parse(json)?.permissions;
    return Array.isArray(perms) ? perms : [];
  } catch {
    return [];
  }
}

/** Cuenta de proveedor de repuestos (y NO de convenio): entra a su propio módulo. */
export function isRepuestosAccount() {
  const perms = getTokenPermissions();
  return perms.includes("REPUESTOS_PORTAL") && !perms.includes("PROVIDER_PORTAL");
}

const repId = (id) => encodeURIComponent(id);

// Subidas: FormData + Content-Type multipart EN ESTA petición (la instancia es
// JSON por defecto y con JSON axios convierte el FormData y el archivo llega
// vacío). El navegador completa el boundary. Más tiempo para redes lentas.
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 };

export async function getRepuestosMe() {
  const { data } = await api.get(`${REP}/me`);
  return data;
}

export async function listAutorizacionesRepuestos(estado) {
  const params = {};
  if (estado) params.estado = estado;
  const { data } = await api.get(`${REP}/autorizaciones`, { params });
  if (Array.isArray(data)) return data;
  return data?.items || [];
}

export async function getAutorizacionRepuestos(id) {
  const { data } = await api.get(`${REP}/autorizaciones/${repId(id)}`);
  return data;
}

export async function recibirAutorizacionRepuestos(id) {
  const { data } = await api.post(`${REP}/autorizaciones/${repId(id)}/recibir`);
  return data;
}

/** Lee el XML sin guardar nada → { invoice, validation }. */
export async function previsualizarFacturaRepuestos(id, xmlFile) {
  const fd = new FormData();
  fd.append("xml", xmlFile);
  const { data } = await api.post(
    `${REP}/autorizaciones/${repId(id)}/factura/previsualizar`,
    fd,
    MULTIPART
  );
  return data;
}

/**
 * Envía la factura. f = { isElectronic, xml, pdf } y, si NO es electrónica,
 * { invoiceNumber, invoiceDate (AAAA-MM-DD), subtotal, tax, total }.
 */
export async function enviarFacturaRepuestos(id, f) {
  const fd = new FormData();
  fd.append("is_electronic", f.isElectronic ? "true" : "false");
  if (f.isElectronic && f.xml) fd.append("xml", f.xml);
  if (f.pdf) fd.append("pdf", f.pdf);
  if (!f.isElectronic) {
    fd.append("invoice_number", f.invoiceNumber);
    fd.append("invoice_date", f.invoiceDate);
    fd.append("subtotal", String(f.subtotal));
    fd.append("tax", String(f.tax));
    fd.append("total", String(f.total));
  }
  const { data } = await api.post(`${REP}/autorizaciones/${repId(id)}/factura`, fd, MULTIPART);
  return data;
}

/** Descarga el archivo subido (tipo "xml" | "pdf") como blob → { blob, filename }. */
export async function descargarArchivoRepuestos(id, tipo) {
  try {
    const res = await api.get(`${REP}/autorizaciones/${repId(id)}/archivo/${tipo}`, {
      responseType: "blob",
      timeout: 120000,
    });
    const cd = String(res.headers?.["content-disposition"] || "");
    const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    let filename = "";
    if (m) {
      const raw = (m[1] || m[2] || "").trim();
      try {
        filename = decodeURIComponent(raw);
      } catch {
        filename = raw;
      }
    }
    return { blob: res.data, filename };
  } catch (e) {
    // Con responseType blob el error también llega como Blob: se lee el JSON
    // para poder mostrar el "detail" del API tal cual.
    const d = e?.response?.data;
    if (d && typeof d.text === "function") {
      try {
        e.response.data = JSON.parse(await d.text());
      } catch {
        /* no era JSON */
      }
    }
    throw e;
  }
}

export default api;
